import { ReactNode, useRef, useState, useId } from 'react'
import { DEFAULT_FOCUS_STYLES, DEFAULT_BORDER_STYLES } from 'css/theme'
import styled from '@emotion/styled'
import type * as CSS from 'csstype'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingArrow,
  arrow,
  Placement,
} from '@floating-ui/react'

const TipBubble = styled.div`
  max-width: 260px;
  width: max-content;
  top: 0;
  left: 0;
  background-color: #312f2d;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
  white-space: normal;
`

const TipHover = styled.span({
  cursor: 'pointer',
  display: 'inline-flex',
  borderRadius: DEFAULT_BORDER_STYLES['--theme--border-radius-sm'],
  '&:focus': {
    outline: DEFAULT_FOCUS_STYLES['--theme--focus-outline'],
    outlineOffset: DEFAULT_FOCUS_STYLES['--theme--focus-outline-offset'],
  },
})

export interface TooltipProps {
  text: string
  placement?: Placement
  openByDefault?: boolean
  children: ReactNode
  textAlign?: CSS.Property.TextAlign
}

export const Tooltip = ({
  text,
  placement = 'bottom',
  children,
  openByDefault = false,
  textAlign = 'center',
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(openByDefault)
  const tipId = useId()
  const arrowRef = useRef(null)

  const isTopOrBottomPlaced = placement.search('top') > -1 || placement.search('bottom') > -1

  const arrowPadding = isTopOrBottomPlaced ? 40 : 10

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(7), flip(), shift(), arrow({ element: arrowRef, padding: arrowPadding })],
    whileElementsMounted: autoUpdate,
    placement: placement,
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return (
    <>
      <TipHover
        ref={refs.setReference}
        {...getReferenceProps()}
        tabIndex={0}
        role="button"
        aria-label={text}
      >
        <span id={tipId} style={{ display: 'none' }}>
          {text}
        </span>
        {children}
      </TipHover>

      {isOpen && (
        <TipBubble
          ref={refs.setFloating}
          id={tipId}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            boxShadow: '0px 2px 2px rgba(27, 25, 24, 0.12)',
            textAlign: textAlign,
          }}
          {...getFloatingProps()}
        >
          <FloatingArrow ref={arrowRef} context={context} fill="#312f2d" />
          {text}
        </TipBubble>
      )}
    </>
  )
}
